/**
 * @fileoverview Modern Backtest Service - wrapper around BacktestApiClient
 * Maintains backward compatibility while using the new API client architecture
 */

import { backtestApi } from '../api/apiClientFactory';
import type { PaginatedResponse } from '../api/core/types';
import type { 
  BacktestRequest,
  OptimizationRequest, 
  SaveBacktestRequest,
  OptimizationResult,
  BacktestResult,
  SavedBacktest
} from '../api/clients/BacktestApiClient';

export class BacktestService {
  /**
   * Run a backtest preview
   */
  async runPreview(request: BacktestRequest): Promise<BacktestResult> {
    return backtestApi.runPreview(request);
  }

  /**
   * Start strategy optimization
   */
  async startOptimization(request: OptimizationRequest): Promise<OptimizationResult> {
    return backtestApi.startOptimization(request);
  }

  /**
   * Get optimization progress
   */
  async getOptimizationProgress(optimizationId: string): Promise<OptimizationResult> {
    return backtestApi.getOptimizationProgress(optimizationId);
  }

  /**
   * Get optimization results
   */
  async getOptimizationResults(optimizationId: string): Promise<OptimizationResult> {
    return backtestApi.getOptimizationResults(optimizationId);
  }

  /**
   * Get saved backtests with pagination
   */
  async getSavedBacktests(options?: { 
    page?: number; 
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<SavedBacktest>> {
    return backtestApi.getSavedBacktests(options);
  }

  /**
   * Get a specific saved backtest by ID
   */
  async getSavedBacktest(backtestId: string): Promise<SavedBacktest> {
    return backtestApi.getSavedBacktest(backtestId);
  }

  /**
   * Save backtest results
   */
  async saveBacktest(request: SaveBacktestRequest): Promise<SavedBacktest> {
    return backtestApi.saveBacktest(request);
  }

  /**
   * Update an existing saved backtest
   */
  async updateSavedBacktest(backtestId: string, updates: Partial<SaveBacktestRequest>): Promise<SavedBacktest> {
    return backtestApi.updateSavedBacktest(backtestId, updates);
  }

  /**
   * Delete a saved backtest
   */
  async deleteSavedBacktest(backtestId: string): Promise<void> {
    return backtestApi.deleteSavedBacktest(backtestId);
  }

  /**
   * Get iteration count for optimization
   */
  async getIterationCount(request: Omit<OptimizationRequest, 'optimizationTarget'>): Promise<{ count: number }> {
    return backtestApi.getIterationCount(request);
  }

  /**
   * Poll for optimization results with automatic retry
   */
  async pollForOptimizationResults(
    optimizationId: string,
    onProgress?: (progress: { completed: boolean; result?: unknown }) => void,
    maxAttempts: number = 600, // 10 minutes (600 seconds) instead of 60 seconds
    interval: number = 2000 // Poll every 2 seconds instead of 1 second for better performance
  ): Promise<any> {
    let attempts = 0;

    const poll = async (): Promise<any> => {
      try {
        // Get optimization progress from API
        const progressData = await this.getOptimizationProgress(optimizationId) as any;
        
        const completed = progressData.completed || false;
        
        // Notify progress callback if provided
        if (onProgress) {
          const progressCallbackData = { 
            completed, 
            result: progressData.result,
            // Pass through the raw server progress data for debugging
            current: progressData.current,
            total: progressData.total,
            progress: progressData.progress
          };
          onProgress(progressCallbackData);
        }

        if (completed) {
          if (progressData.result) {
            return progressData.result;
          } else if (progressData.error) {
            throw new Error(progressData.error);
          }
        }

        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error('Optimization timeout - maximum attempts reached');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
        return poll();
      } catch (error) {
        throw error;
      }
    };

    return poll();
  }
}

// Export singleton instance
export const backtestService = new BacktestService();
